import {
  BookmarkIcon,
  ImportIcon,
  CloseIcon,
} from '@maersktankersdigital/web-components'
import { format } from 'date-fns'
import TagManager from 'react-gtm-module'
import { useAddBookmark } from '~hooks/queries/documents/use-add-bookmark'
import { useGetDocument } from '~hooks/queries/documents/use-get-document'
import { IDocument } from '~hooks/queries/documents/use-get-documents'
import { useRemoveBookmark } from '~hooks/queries/documents/use-remove-bookmark'
import {
  BookmarkWrapper,
  ContentWrapper,
  DocumentButtons,
  DocumentInfo,
  DocumentSeparator,
  DocumentWrapper,
  FileIconWrapper,
  StyledBookmarkFilledIcon,
} from '~pages/pages-behind-login/documents/documents.styles'
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContentText,
  IconButton,
  DialogContent,
} from '@mui/material'
import { getFileIcon } from '~pages/pages-behind-login/documents/file-type/file-type-icon-helper'
import { trackDownloadButtonClick } from '../calendar/tracking/tracking-calendar'
import { useEffect, useState } from 'react'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'

export function Document({ singleDocument }: { singleDocument: IDocument }) {
  const [previewOpen, setPreviewOpen] = useState(false)
  const { mutate: removeBookmark, status: removeBookmarkStatus } =
    useRemoveBookmark()
  const { mutate: addBookmark, status: addBookmarkStatus } = useAddBookmark()
  const { setStatus: setAnimatedAlertStatus } = useAnimatedAlert()
  const [bookmarkState, setBookmarkState] = useState<undefined | boolean>(
    singleDocument.userBookmark,
  )
  useEffect(() => {
    if (addBookmarkStatus === 'error' || removeBookmarkStatus === 'error') {
      setAnimatedAlertStatus('Updating bookmark failed.', undefined, 'error')
    }
    if (addBookmarkStatus === 'success' || removeBookmarkStatus === 'success') {
      setBookmarkState(!bookmarkState)
      setAnimatedAlertStatus('Updating bookmark success.', undefined, 'success')
    }
  }, [addBookmarkStatus, removeBookmarkStatus])

  const { refetch: fetchDownloadLink } = useGetDocument({
    documentId: singleDocument?._id,
    attachment: true,
  })

  const { refetch: fetchPreviewLink } = useGetDocument({
    documentId: singleDocument?._id,
    attachment: false,
  })

  const [previewLink, setPreviewLink] = useState<string | null>(null)

  const onClickHandler = async ({
    action,
  }: {
    action: 'preview' | 'download'
  }) => {
    let documentInfo
    if (action === 'preview') {
      const { data } = await fetchPreviewLink()
      documentInfo = data
      setPreviewOpen(true)
      setPreviewLink(documentInfo?.signedUrl || null)
    } else {
      const { data } = await fetchDownloadLink()
      documentInfo = data
      if (documentInfo?.signedUrl) {
        window.open(documentInfo.signedUrl, '_blank', 'noopener,noreferrer')
      }
    }
  }

  const documentExtension =
    singleDocument?.name?.split('.').pop()?.toLowerCase() || ''

  const showFullScreen =
    documentExtension === 'pdf' ||
    documentExtension === 'png' ||
    documentExtension === 'jpg' ||
    documentExtension === 'jpeg'

  const isImageFile =
    documentExtension === 'png' ||
    documentExtension === 'jpg' ||
    documentExtension === 'jpeg'

  return (
    <>
      <Dialog
        fullScreen={showFullScreen}
        onClose={() => setPreviewOpen(false)}
        open={previewOpen}
        sx={{ mt: 8, mb: 10, mx: 10 }}
      >
        <IconButton
          aria-label="close"
          onClick={() => setPreviewOpen(false)}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle>{singleDocument.title || 'No Document Title'}</DialogTitle>
        {isImageFile ? (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <img
              src={previewLink || ''}
              alt={singleDocument.title}
              style={{
                maxHeight: '400px',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        ) : documentExtension === 'pdf' ? (
          <embed
            src={previewLink || ''}
            type={`application/${documentExtension}`}
            style={{ height: '100%' }}
          />
        ) : (
          <DialogContent>
            <DialogContentText>
              Preview is not available for this file type. Please download the
              file instead.
            </DialogContentText>
          </DialogContent>
        )}
        <DialogActions>
          <Button variant="contained" onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => onClickHandler({ action: 'download' })}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
      <DocumentWrapper
        key={singleDocument._id}
        onClickHandler={() => onClickHandler({ action: 'preview' })}
      >
        <ContentWrapper>
          <FileIconWrapper>{getFileIcon(documentExtension)}</FileIconWrapper>
          <DocumentInfo
            publishDate={
              singleDocument?.uploadedDate
                ? format(new Date(singleDocument?.uploadedDate), 'dd MMM yyyy')
                : 'Invalid Date'
            }
            title={singleDocument.title || 'No Document Title'}
            category={singleDocument.category}
          />
        </ContentWrapper>
        <DocumentSeparator />
        <DocumentButtons>
          <ImportIcon
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              onClickHandler({ action: 'download' })
            }}
          />
          <BookmarkWrapper
            onClick={(e) => {
              e.stopPropagation()
              if (singleDocument.userBookmark) {
                trackBookmarkButtonClick(singleDocument.title, 'removeBookmark')
                removeBookmark(singleDocument._id)
              } else {
                trackBookmarkButtonClick(singleDocument.title, 'addBookmark')
                addBookmark({ documentId: singleDocument._id })
              }
              // Crazy state management!
              // But it works.
              // singleDocument.userBookmark = !singleDocument.userBookmark
            }}
          >
            {bookmarkState ? <StyledBookmarkFilledIcon /> : <BookmarkIcon />}
          </BookmarkWrapper>
        </DocumentButtons>
      </DocumentWrapper>
    </>
  )
}

// This function is no longer used for this component, but it is imported from here in two other files.
// It is possible these other components no longer need it either,
// since Azure blob storage takes care of the download/preview functionality via Content-Disposition header.
export function downloadFile({ url, title }: { url: string; title: string }) {
  trackDownloadButtonClick(title)

  const a = document.createElement('a')
  a.href = url
  a.target = '_blank'
  a.download = title
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

function trackBookmarkButtonClick(
  title: string,
  type: 'removeBookmark' | 'addBookmark',
) {
  const tagManagerArgs = {
    dataLayer: {
      event: 'internal_click',
      click_type:
        type === 'removeBookmark'
          ? 'Remove Bookmark from Document'
          : 'Bookmark Document',
      click_subtype: title,
    },
  }
  TagManager.dataLayer(tagManagerArgs)
}
